// Pages/ProductsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useProductStore } from "./../../store/productStore";
import {
  BiPlus,
  BiEdit,
  BiTrash,
  BiShow,
  BiHistory,
  BiTrendingUp,
  BiTrendingDown,
  BiAdjust,
  BiX,
  BiPackage,
  BiCheckCircle,
  BiXCircle,
  BiChevronLeft,
  BiChevronRight,
} from "react-icons/bi";
import {
  FiAlertTriangle,
  FiTrash2,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiX,
  FiSearch,
} from "react-icons/fi";

export default function ProductsPage() {
  const {
    products,
    loading,
    currentProduct,
    fetchProducts,
    fetchProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    createMovement,
    fetchProductHistory,
    historyReport,
  } = useProductStore();

  const [showProductModal, setShowProductModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [movementType, setMovementType] = useState("ENTREE");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // États pour le formulaire produit
  const [productForm, setProductForm] = useState({
    libelle: "",
    description: "",
    quantity: 0,
    seuilAlerte: 5,
    isActive: true,
  });

  // États pour le formulaire mouvement
  const [movementForm, setMovementForm] = useState({
    productId: "",
    type: "ENTREE",
    quantity: 0,
    motif: "",
  });

  // États pour la recherche et filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  const [isEdit, setIsEdit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      await fetchProducts();
    } catch (error) {
      console.error("Erreur lors du chargement des produits:", error);
    }
  }, [fetchProducts]);

  // Chargement initial
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Réinitialiser le formulaire produit
  const resetProductForm = () => {
    setProductForm({
      libelle: "",
      description: "",
      quantity: 0,
      seuilAlerte: 5,
      isActive: true,
    });
  };

  // Réinitialiser le formulaire mouvement
  const resetMovementForm = () => {
    setMovementForm({
      productId: "",
      type: "ENTREE",
      quantity: 0,
      motif: "",
    });
  };

  // Ouvrir modal d'ajout de produit
  const handleAddProduct = () => {
    setIsEdit(false);
    resetProductForm();
    setShowProductModal(true);
  };

  // Ouvrir modal d'édition de produit
  const handleEditProduct = (product) => {
    setIsEdit(true);
    setSelectedProduct(product);
    setProductForm({
      libelle: product.libelle,
      description: product.description || "",
      quantity: product.quantity,
      seuilAlerte: product.seuilAlerte,
      isActive: product.isActive,
    });
    setShowProductModal(true);
  };

  // Ouvrir modal de détails
  const handleShowDetails = async (product) => {
    setModalLoading(true);
    try {
      await fetchProductById(product.id);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement des détails");
    } finally {
      setModalLoading(false);
    }
  };

  // Ouvrir modal d'historique
  const handleShowHistory = async (product) => {
    setModalLoading(true);
    try {
      await fetchProductHistory(product.id);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setModalLoading(false);
    }
  };

  // Ouvrir modal de mouvement
  const handleShowMovement = (product, type) => {
    setMovementType(type);
    setMovementForm({
      productId: product.id,
      type: type,
      quantity: 0,
      motif: "",
    });
    setShowMovementModal(true);
  };

  // Soumettre le formulaire produit
  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      await createProduct(productForm);
      toast.success("Produit créé avec succès");
      setShowProductModal(false);
      resetProductForm();
      await loadProducts();
    } catch (error) {
      toast.error("Erreur lors de la création du produit");
    } finally {
      setSelectedProduct(null);
      setModalLoading(false);
    }
  };

  // Soumettre le formulaire de modification de produit
  const handleUpdateSubmitProduct = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error("Erreur lors de la modification du produit");
      return;
    }
    setModalLoading(true);

    try {
      console.log({ productForm });
      await updateProduct(selectedProduct.id, productForm);
      toast.success("Produit modifié avec succès");
      setShowProductModal(false);
      resetProductForm();
      await loadProducts();
    } catch (error) {
      toast.error("Erreur lors de la modification du produit");
    } finally {
      setModalLoading(false);
      setIsEdit(false);
      setSelectedProduct(null);
    }
  };

  // Soumettre le formulaire mouvement
  const handleSubmitMovement = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      await createMovement(movementForm);
      toast.success("Mouvement enregistré avec succès");
      setShowMovementModal(false);
      resetMovementForm();
      await loadProducts(); // Recharger pour mettre à jour les quantités
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du mouvement");
    } finally {
      setModalLoading(false);
    }
  };

  // Supprimer un produit
  const handleDeleteProduct = async (product) => {
    setDeleteConfirm(product);
  };

  // Confirmer la suppression
  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteProduct(deleteConfirm.id);
      toast.success("Produit supprimé avec succès");
      setDeleteConfirm(null);
      await loadProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression du produit");
      setDeleteConfirm(null);
    }
  };

  // Obtenir le statut du stock
  const getStockStatus = (product) => {
    if (product.quantity === 0) return "out-of-stock";
    if (product.quantity <= product.seuilAlerte) return "low-stock";
    return "in-stock";
  };

  const getStockStatusLabel = (product) => {
    const status = getStockStatus(product);
    switch (status) {
      case "out-of-stock":
        return "Rupture";
      case "low-stock":
        return "Faible";
      default:
        return "Normal";
    }
  };

  const getStockStatusColor = (product) => {
    const status = getStockStatus(product);
    switch (status) {
      case "out-of-stock":
        return "bg-red-50 text-red-700 border-red-200";
      case "low-stock":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      default:
        return "bg-green-50 text-green-700 border-green-200";
    }
  };

  const getMovementTypeLabel = (type) => {
    switch (type) {
      case "ENTREE":
        return "Entrée";
      case "SORTIE":
        return "Sortie";
      case "AJUSTEMENT":
        return "Ajustement";
      case "PERDU":
        return "Perte";
      default:
        return type;
    }
  };

  const getMovementTypeIcon = (type) => {
    switch (type) {
      case "ENTREE":
        return <BiTrendingUp className="text-green-600" size={14} />;
      case "SORTIE":
        return <BiTrendingDown className="text-red-600" size={14} />;
      case "AJUSTEMENT":
        return <BiAdjust className="text-blue-600" size={14} />;
      case "PERDU":
        return <BiXCircle className="text-orange-600" size={14} />;
      default:
        return <BiPackage className="text-gray-600" size={14} />;
    }
  };

  // Filtrer les produits
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      !searchTerm ||
      product.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" && product.isActive) ||
      (statusFilter === "inactive" && !product.isActive);

    const matchesStock =
      !stockFilter ||
      (stockFilter === "low-stock" &&
        getStockStatus(product) === "low-stock") ||
      (stockFilter === "out-of-stock" &&
        getStockStatus(product) === "out-of-stock") ||
      (stockFilter === "in-stock" && getStockStatus(product) === "in-stock");

    return matchesSearch && matchesStatus && matchesStock;
  });

  // Statistiques
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.isActive).length;
  const lowStockProducts = products.filter(
    (p) => getStockStatus(p) === "low-stock",
  ).length;
  const outOfStockProducts = products.filter(
    (p) => getStockStatus(p) === "out-of-stock",
  ).length;

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Composant Card pour mobile/tablette
  const ProductCard = ({ product }) => (
    <div className="bg-white border border-gray-200 hover:shadow-md transition-shadow">
      <div className="p-4 border-b border-gray-100">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 ${product.isActive ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"}`}
            >
              <BiPackage size={20} />
            </div>
            <div>
              <h3 className="font-poppins font-semibold text-sm text-title">
                {product.libelle}
              </h3>
              <p className="font-nunito text-xs text-gray-500 truncate max-w-[200px]">
                {product.description || "Aucune description"}
              </p>
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs font-semibold border ${getStockStatusColor(product)}`}
          >
            {getStockStatusLabel(product)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <p className="font-nunito text-xs text-gray-500">Stock actuel</p>
            <p className="font-poppins font-bold text-base">
              {product.quantity}
            </p>
          </div>
          <div>
            <p className="font-nunito text-xs text-gray-500">Seuil d'alerte</p>
            <p className="font-poppins font-medium text-sm">
              {product.seuilAlerte}
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span
            className={`px-2 py-1 text-xs ${product.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
          >
            {product.isActive ? "Actif" : "Inactif"}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => handleShowDetails(product)}
              className="p-2 text-blue-600 hover:bg-blue-50"
              title="Détails"
            >
              <BiShow size={16} />
            </button>
            <button
              onClick={() => handleEditProduct(product)}
              className="p-2 text-green-600 hover:bg-green-50"
              title="Modifier"
            >
              <BiEdit size={16} />
            </button>
            <button
              onClick={() => handleDeleteProduct(product)}
              className="p-2 text-red-600 hover:bg-red-50"
              title="Supprimer"
            >
              <BiTrash size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-3 bg-gray-50">
        <div className="flex gap-2">
          <button
            onClick={() => handleShowMovement(product, "ENTREE")}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 hover:bg-green-100"
          >
            <BiTrendingUp size={12} />
            Entrée
          </button>
          <button
            onClick={() => handleShowMovement(product, "SORTIE")}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100"
          >
            <BiTrendingDown size={12} />
            Sortie
          </button>
          <button
            onClick={() => handleShowHistory(product)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100"
          >
            <BiHistory size={12} />
            Historique
          </button>
        </div>
      </div>
    </div>
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Produit",
      width: "30%",
      render: (product) => (
        <div className="flex items-center gap-3">
          <div
            className={`p-2 ${product.isActive ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"}`}
          >
            <BiPackage size={18} />
          </div>
          <div>
            <p className="font-poppins font-semibold text-sm text-title">
              {product.libelle}
            </p>
            <p className="font-nunito text-xs text-gray-500 truncate max-w-[200px]">
              {product.description || "Aucune description"}
            </p>
          </div>
        </div>
      ),
    },
    {
      headerName: "Stock",
      width: "15%",
      render: (product) => (
        <div>
          <p className="font-poppins font-bold text-base">{product.quantity}</p>
          <p className="font-nunito text-xs text-gray-500">
            Seuil: {product.seuilAlerte}
          </p>
        </div>
      ),
    },
    {
      headerName: "Statut Stock",
      width: "15%",
      render: (product) => (
        <span
          className={`px-2 py-1 text-xs font-semibold border ${getStockStatusColor(product)}`}
        >
          {getStockStatusLabel(product)}
        </span>
      ),
    },
    {
      headerName: "Statut",
      width: "10%",
      render: (product) => (
        <span
          className={`px-2 py-1 text-xs ${product.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
        >
          {product.isActive ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      headerName: "Actions",
      width: "30%",
      render: (product) => (
        <div className="flex items-center gap-2">
          <button
            title="Détails"
            onClick={() => handleShowDetails(product)}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
            <BiShow size={12} />
          </button>
          <button
            title="Historique"
            onClick={() => handleShowHistory(product)}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100"
          >
            <BiHistory size={12} />
          </button>
          <button
            title="Modifier"
            onClick={() => handleEditProduct(product)}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-green-50 text-green-700 hover:bg-green-100"
          >
            <BiEdit size={12} />
          </button>
          <div className="relative group">
            <button className="flex items-center gap-1 px-3 py-1 text-xs bg-primary text-white hover:bg-primary/90">
              <BiTrendingUp size={12} />
              Mouvement
            </button>
            <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <button
                onClick={() => handleShowMovement(product, "ENTREE")}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-green-700 hover:bg-green-50"
              >
                <BiTrendingUp size={12} />
                Entrée stock
              </button>
              <button
                onClick={() => handleShowMovement(product, "SORTIE")}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-700 hover:bg-red-50"
              >
                <BiTrendingDown size={12} />
                Sortie stock
              </button>
              <button
                onClick={() => handleShowMovement(product, "AJUSTEMENT")}
                className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-xs text-blue-700 hover:bg-blue-50"
              >
                <BiAdjust size={12} />
                Ajustement
              </button>
              <button
                onClick={() => handleShowMovement(currentProduct, "PERDU")}
                className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-xs text-orange-700 hover:bg-orange-50"
              >
                <BiXCircle size={14} />
                Marquer perte
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-poppins font-semibold text-xl text-title">
              Gestion des Produits
            </h1>
            <p className="font-nunito text-sm text-gray-600 mt-1">
              Gérez l'inventaire de vos produits et suivez les mouvements de
              stock
            </p>
          </div>
          <button
            onClick={handleAddProduct}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white hover:bg-primary/90 transition-colors font-nunito text-sm font-semibold"
          >
            <BiPlus size={16} />
            Ajouter un produit
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiPackage className="text-primary" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Total produits
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {totalProducts}
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiCheckCircle className="text-green-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Produits actifs
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {activeProducts}
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <FiAlertTriangle className="text-yellow-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Stock faible
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {lowStockProducts}
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiXCircle className="text-red-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Rupture de stock
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {outOfStockProducts}
          </p>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FiSearch className="text-gray-400" size={18} />
            </div>
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <FiX className="text-gray-400 hover:text-gray-600" size={18} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:border-primary font-nunito text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>

            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:border-primary font-nunito text-sm"
            >
              <option value="">Tous les stocks</option>
              <option value="in-stock">Stock normal</option>
              <option value="low-stock">Stock faible</option>
              <option value="out-of-stock">Rupture</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 font-nunito text-sm"
            >
              <FiFilter size={16} />
              Plus de filtres
            </button>

            {(searchTerm || statusFilter || stockFilter) && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setStockFilter("");
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 font-nunito text-sm"
              >
                <FiRefreshCw size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Pagination en haut */}
      {totalPages > 1 && filteredProducts.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
          <div className="font-nunito text-sm text-gray-600">
            Page {currentPage} sur {totalPages} • {filteredProducts.length}{" "}
            produit{filteredProducts.length > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-nunito ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Tableau Desktop */}
      <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left font-poppins font-semibold text-xs text-gray-700 border-b border-gray-200"
                    style={{ width: column.width }}
                  >
                    {column.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedProducts.map((product, index) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className="px-6 py-4">
                      <div className="flex items-center h-full">
                        {column.render(product)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cartes Mobile/Tablette */}
      <div className="lg:hidden space-y-4 mb-6">
        {paginatedProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {/* Pagination en bas */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
          <div className="font-nunito text-sm text-gray-600">
            Page {currentPage} sur {totalPages} • {filteredProducts.length}{" "}
            produit{filteredProducts.length > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-nunito ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Message vide */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200">
          <BiPackage className="mx-auto text-gray-300" size={48} />
          <p className="font-nunito text-sm text-gray-600 mt-3">
            {searchTerm || statusFilter || stockFilter
              ? "Aucun produit ne correspond à vos critères"
              : "Aucun produit enregistré"}
          </p>
          {searchTerm || statusFilter || stockFilter ? (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
                setStockFilter("");
              }}
              className="mt-4 px-4 py-2 bg-primary text-white font-nunito text-sm hover:bg-primary/90"
            >
              Réinitialiser les filtres
            </button>
          ) : (
            <button
              onClick={handleAddProduct}
              className="mt-4 px-4 py-2 bg-primary text-white font-nunito text-sm hover:bg-primary/90"
            >
              Ajouter un produit
            </button>
          )}
        </div>
      )}

      {/* Modal Ajout/Modification Produit */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary text-white">
              <h2 className="font-poppins font-semibold text-lg">
                {isEdit ? "Modifier le produit" : "Ajouter un produit"}
              </h2>
              <button
                onClick={() => setShowProductModal(false)}
                className="p-1 hover:bg-white/20 transition-colors"
              >
                <BiX size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <form
                onSubmit={
                  isEdit ? handleUpdateSubmitProduct : handleSubmitProduct
                }
                className="p-4 space-y-4"
              >
                <div>
                  <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                    Nom du produit *
                  </label>
                  <input
                    type="text"
                    required
                    value={productForm.libelle}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        libelle: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                  />
                </div>

                <div>
                  <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                  />
                </div>

                <div
                  className={`grid ${!isEdit ? "grid-cols-2" : "grid-cols-1"} gap-4`}
                >
                  {!isEdit && (
                    <div>
                      <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                        Quantité initiale
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={productForm.quantity}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            quantity: parseInt(e.target.value),
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                      Seuil d'alerte
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={productForm.seuilAlerte}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          seuilAlerte: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={productForm.isActive}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        isActive: e.target.checked,
                      })
                    }
                    className="border-gray-300 text-primary focus:ring-primary"
                  />
                  <label className="font-poppins font-medium text-sm text-gray-700">
                    Produit actif
                  </label>
                </div>
              </form>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="px-4 py-2 font-nunito text-sm font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  onClick={
                    isEdit ? handleUpdateSubmitProduct : handleSubmitProduct
                  }
                  disabled={modalLoading}
                  className="px-4 py-2 font-nunito text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {isEdit
                    ? modalLoading
                      ? "Modification..."
                      : "Modifier le produit"
                    : modalLoading
                      ? "Création..."
                      : "Créer le produit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détails Produit */}
      {showDetailsModal && currentProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary text-white">
              <h2 className="font-poppins font-semibold text-lg">
                Détails du produit
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-1 hover:bg-white/20 transition-colors"
              >
                <BiX size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="p-4 space-y-4">
                {modalLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <p className="font-poppins font-medium text-sm text-gray-500">
                            Nom
                          </p>
                          <p className="font-poppins font-semibold text-lg text-gray-900">
                            {currentProduct.libelle}
                          </p>
                        </div>
                        <div>
                          <p className="font-poppins font-medium text-sm text-gray-500">
                            Description
                          </p>
                          <p className="font-nunito text-sm text-gray-700">
                            {currentProduct.description || "Aucune description"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="font-poppins font-medium text-sm text-gray-500">
                            Stock actuel
                          </p>
                          <p className="font-poppins font-bold text-xl text-gray-900">
                            {currentProduct.quantity}
                          </p>
                        </div>
                        <div>
                          <p className="font-poppins font-medium text-sm text-gray-500">
                            Seuil d'alerte
                          </p>
                          <p className="font-poppins font-semibold text-lg text-gray-900">
                            {currentProduct.seuilAlerte}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-poppins font-medium text-sm text-gray-500">
                          Statut stock
                        </p>
                        <span
                          className={`px-2 py-1 font-nunito text-xs font-medium border ${getStockStatusColor(
                            currentProduct,
                          )}`}
                        >
                          {getStockStatusLabel(currentProduct)}
                        </span>
                      </div>
                      <div>
                        <p className="font-poppins font-medium text-sm text-gray-500">
                          Statut produit
                        </p>
                        <span
                          className={`px-2 py-1 font-nunito text-xs font-medium ${
                            currentProduct.isActive
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {currentProduct.isActive ? "Actif" : "Inactif"}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleShowHistory(currentProduct);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-50 text-purple-700 hover:bg-purple-100"
                >
                  <BiHistory size={14} />
                  Historique
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEditProduct(currentProduct);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 text-green-700 hover:bg-green-100"
                >
                  <BiEdit size={14} />
                  Modifier
                </button>
                <div className="relative group">
                  <button className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white hover:bg-primary/90">
                    <BiTrendingUp size={14} />
                    Mouvement
                  </button>
                  <div className="absolute left-0 bottom-full mb-1 w-48 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleShowMovement(currentProduct, "ENTREE");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-green-700 hover:bg-green-50"
                    >
                      <BiTrendingUp size={14} />
                      Entrée stock
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleShowMovement(currentProduct, "SORTIE");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                    >
                      <BiTrendingDown size={14} />
                      Sortie stock
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleShowMovement(currentProduct, "AJUSTEMENT");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                    >
                      <BiAdjust size={14} />
                      Ajustement
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleShowMovement(currentProduct, "PERDU");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-[14px] text-orange-700 hover:bg-orange-50"
                    >
                      <BiXCircle size={14} />
                      Marquer perte
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleDeleteProduct(currentProduct);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 text-red-700 hover:bg-red-100"
                >
                  <BiTrash size={14} />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary text-white">
              <h2 className="font-poppins font-semibold text-lg">
                Historique des mouvements
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-1 hover:bg-white/20 transition-colors"
              >
                <BiX size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="p-4">
                {modalLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : historyReport.length === 0 ? (
                  <p className="text-center font-nunito text-sm text-gray-600 py-8">
                    Aucun mouvement enregistré
                  </p>
                ) : (
                  <div className="space-y-3">
                    {historyReport.map((movement, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 border border-gray-200 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          {getMovementTypeIcon(movement.type)}
                          <div>
                            <p className="font-poppins font-semibold text-sm text-gray-900">
                              {getMovementTypeLabel(movement.type)}
                            </p>
                            <p className="font-nunito text-xs text-gray-600">
                              {movement.motif}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-poppins font-bold text-base ${
                              movement.type === "ENTREE"
                                ? "text-green-600"
                                : movement.type === "AJUSTEMENT"
                                  ? "text-gray-600"
                                  : "text-red-600"
                            }`}
                          >
                            {movement.type === "ENTREE"
                              ? "+"
                              : movement.type === "AJUSTEMENT"
                                ? ""
                                : "-"}
                            {movement.quantity}
                          </p>
                          <p className="font-nunito text-xs text-gray-500">
                            {new Date(movement.created_at).toLocaleDateString(
                              "fr-FR",
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 font-nunito text-sm font-semibold bg-primary text-white hover:bg-primary/90"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mouvement de stock */}
      {showMovementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary text-white">
              <h2 className="font-poppins font-semibold text-lg">
                {movementType === "ENTREE" && "Entrée de stock"}
                {movementType === "SORTIE" && "Sortie de stock"}
                {movementType === "AJUSTEMENT" && "Ajustement de stock"}
                {movementType === "PERDU" && "Marquer comme perdu"}
              </h2>
              <button
                onClick={() => setShowMovementModal(false)}
                className="p-1 hover:bg-white/20 transition-colors"
              >
                <BiX size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <form onSubmit={handleSubmitMovement} className="p-4 space-y-4">
                <div>
                  <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                    Quantité *
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={movementForm.quantity}
                    onChange={(e) =>
                      setMovementForm({
                        ...movementForm,
                        quantity: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                  />
                </div>

                <div>
                  <label className="block font-poppins font-medium text-sm text-gray-700 mb-1">
                    Motif *
                  </label>
                  <textarea
                    required
                    value={movementForm.motif}
                    onChange={(e) =>
                      setMovementForm({
                        ...movementForm,
                        motif: e.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Raison du mouvement..."
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
                  />
                </div>
              </form>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowMovementModal(false)}
                  className="px-4 py-2 font-nunito text-sm font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  onClick={handleSubmitMovement}
                  disabled={modalLoading}
                  className="px-4 py-2 font-nunito text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {modalLoading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-50">
                  <FiTrash2 className="text-red-500" size={24} />
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900">
                  Confirmer la suppression
                </h3>
              </div>
              <p className="font-nunito text-sm text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer le produit "
                {deleteConfirm.libelle}" ? Cette action est irréversible.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 font-semibold font-nunito text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 font-semibold font-nunito text-sm"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// // Pages/ProductsPage.jsx
// import React, { useCallback, useEffect, useState } from "react";
// import { toast } from "react-toastify";
// import { useProductStore } from "./../../store/productStore";
// import {
//   BiPlus,
//   BiEdit,
//   BiTrash,
//   BiShow,
//   BiHistory,
//   BiTrendingUp,
//   BiTrendingDown,
//   BiAdjust,
//   BiX,
//   BiPackage,
//   BiCheckCircle,
//   BiXCircle,
// } from "react-icons/bi";
// import DataTable from "../../common/DataTable";
// import { FiAlertTriangle, FiTrash2 } from "react-icons/fi";
// import { GridActionsCellItem } from "@mui/x-data-grid";

// export default function ProductsPage() {
//   const {
//     products,
//     loading,
//     currentProduct,
//     fetchProducts,
//     fetchProductById,
//     createProduct,
//     updateProduct,
//     deleteProduct,
//     createMovement,
//     fetchProductHistory,
//     historyReport,
//   } = useProductStore();

//   const [showProductModal, setShowProductModal] = useState(false);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [showHistoryModal, setShowHistoryModal] = useState(false);
//   const [showMovementModal, setShowMovementModal] = useState(false);
//   const [modalLoading, setModalLoading] = useState(false);
//   const [movementType, setMovementType] = useState("ENTREE");
//   const [deleteConfirm, setDeleteConfirm] = useState(null);

//   // États pour le formulaire produit
//   const [productForm, setProductForm] = useState({
//     libelle: "",
//     description: "",
//     quantity: 0,
//     seuilAlerte: 5,
//     isActive: true,
//   });

//   // États pour le formulaire mouvement
//   const [movementForm, setMovementForm] = useState({
//     productId: "",
//     type: "ENTREE",
//     quantity: 0,
//     motif: "",
//   });

//   // États pour la recherche et filtres
//   const [searchTerm, setSearchTerm] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");

//   const loadProducts = useCallback(async () => {
//     try {
//       await fetchProducts();
//     } catch (error) {
//       console.error("Erreur lors du chargement des produits:", error);
//     }
//   }, [fetchProducts]);

//   // Chargement initial
//   useEffect(() => {
//     loadProducts();
//   }, [loadProducts]);

//   // Réinitialiser le formulaire produit
//   const resetProductForm = () => {
//     setProductForm({
//       libelle: "",
//       description: "",
//       quantity: 0,
//       seuilAlerte: 5,
//       isActive: true,
//     });
//   };

//   // Réinitialiser le formulaire mouvement
//   const resetMovementForm = () => {
//     setMovementForm({
//       productId: "",
//       type: "ENTREE",
//       quantity: 0,
//       motif: "",
//     });
//   };

//   // Ouvrir modal d'ajout de produit
//   const handleAddProduct = () => {
//     setIsEdit(false);
//     resetProductForm();
//     setShowProductModal(true);
//   };

//   const [isEdit, setIsEdit] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   // Ouvrir modal d'édition de produit
//   const handleEditProduct = (product) => {
//     setIsEdit(true);
//     setSelectedProduct(product);
//     setProductForm({
//       libelle: product.libelle,
//       description: product.description || "",
//       quantity: product.quantity,
//       seuilAlerte: product.seuilAlerte,
//       isActive: product.isActive,
//     });
//     setShowProductModal(true);
//   };

//   // Ouvrir modal de détails
//   const handleShowDetails = async (product) => {
//     setModalLoading(true);
//     try {
//       await fetchProductById(product.id);
//       setShowDetailsModal(true);
//     } catch (error) {
//       toast.error("Erreur lors du chargement des détails");
//     } finally {
//       setModalLoading(false);
//     }
//   };

//   // Ouvrir modal d'historique
//   const handleShowHistory = async (product) => {
//     setModalLoading(true);
//     try {
//       await fetchProductHistory(product.id);
//       setShowHistoryModal(true);
//     } catch (error) {
//       toast.error("Erreur lors du chargement de l'historique");
//     } finally {
//       setModalLoading(false);
//     }
//   };

//   // Ouvrir modal de mouvement
//   const handleShowMovement = (product, type) => {
//     setMovementType(type);
//     setMovementForm({
//       productId: product.id,
//       type: type,
//       quantity: 0,
//       motif: "",
//     });
//     setShowMovementModal(true);
//   };

//   // Soumettre le formulaire produit
//   const handleSubmitProduct = async (e) => {
//     e.preventDefault();
//     setModalLoading(true);

//     try {
//       await createProduct(productForm);
//       toast.success("Produit créé avec succès");
//       setShowProductModal(false);
//       resetProductForm();
//       await loadProducts();
//     } catch (error) {
//       toast.error("Erreur lors de la création du produit");
//     } finally {
//       setSelectedProduct(null);
//       setModalLoading(false);
//     }
//   };

//   // Soumettre le formulaire de modification de produit
//   const handleUpdateSubmitProduct = async (e) => {
//     e.preventDefault();
//     if (!selectedProduct) {
//       toast.error("Erreur lors de la modification du produit");
//       return;
//     }
//     setModalLoading(true);

//     try {
//       console.log({ productForm });
//       await updateProduct(selectedProduct.id, productForm);
//       toast.success("Produit modifié avec succès");
//       setShowProductModal(false);
//       resetProductForm();
//       await loadProducts();
//     } catch (error) {
//       toast.error("Erreur lors de la modification du produit");
//     } finally {
//       setModalLoading(false);
//       setIsEdit(false);
//       setSelectedProduct(null);
//     }
//   };

//   // Soumettre le formulaire mouvement
//   const handleSubmitMovement = async (e) => {
//     e.preventDefault();
//     setModalLoading(true);

//     try {
//       await createMovement(movementForm);
//       toast.success("Mouvement enregistré avec succès");
//       setShowMovementModal(false);
//       resetMovementForm();
//       await loadProducts(); // Recharger pour mettre à jour les quantités
//     } catch (error) {
//       toast.error("Erreur lors de l'enregistrement du mouvement");
//     } finally {
//       setModalLoading(false);
//     }
//   };

//   // Supprimer un produit
//   const handleDeleteProduct = async (product) => {
//     setDeleteConfirm(product);
//   };

//   // Confirmer la suppression
//   const handleConfirmDelete = async () => {
//     if (!deleteConfirm) return;

//     try {
//       await deleteProduct(deleteConfirm.id);
//       toast.success("Produit supprimé avec succès");
//       setDeleteConfirm(null);
//       await loadProducts();
//     } catch (error) {
//       toast.error("Erreur lors de la suppression du produit");
//       setDeleteConfirm(null);
//     }
//   };

//   // Obtenir le statut du stock
//   const getStockStatus = (product) => {
//     if (product.quantity === 0) return "out-of-stock";
//     if (product.quantity <= product.seuilAlerte) return "low-stock";
//     return "in-stock";
//   };

//   const getStockStatusLabel = (product) => {
//     const status = getStockStatus(product);
//     switch (status) {
//       case "out-of-stock":
//         return "Rupture de stock";
//       case "low-stock":
//         return "Stock faible";
//       default:
//         return "En stock";
//     }
//   };

//   const getStockStatusColor = (product) => {
//     const status = getStockStatus(product);
//     switch (status) {
//       case "out-of-stock":
//         return "bg-red-100 text-red-800";
//       case "low-stock":
//         return "bg-yellow-100 text-yellow-800";
//       default:
//         return "bg-green-100 text-green-800";
//     }
//   };

//   const getMovementTypeLabel = (type) => {
//     switch (type) {
//       case "ENTREE":
//         return "Entrée";
//       case "SORTIE":
//         return "Sortie";
//       case "AJUSTEMENT":
//         return "Ajustement";
//       case "PERDU":
//         return "Perte";
//       default:
//         return type;
//     }
//   };

//   // const getMovementTypeColor = (type) => {
//   //   switch (type) {
//   //     case "ENTREE":
//   //       return "text-green-600 bg-green-50";
//   //     case "SORTIE":
//   //       return "text-red-600 bg-red-50";
//   //     case "AJUSTEMENT":
//   //       return "text-blue-600 bg-blue-50";
//   //     case "PERDU":
//   //       return "text-orange-600 bg-orange-50";
//   //     default:
//   //       return "text-gray-600 bg-gray-50";
//   //   }
//   // };

//   const getMovementTypeIcon = (type) => {
//     switch (type) {
//       case "ENTREE":
//         return <BiTrendingUp size={14} />;
//       case "SORTIE":
//         return <BiTrendingDown size={14} />;
//       case "AJUSTEMENT":
//         return <BiAdjust size={14} />;
//       case "PERDU":
//         return <BiXCircle size={14} />;
//       default:
//         return <BiPackage size={14} />;
//     }
//   };

//   // Colonnes du tableau
//   const columns = [
//     {
//       field: "libelle",
//       headerName: "Nom du produit",
//       width: 200,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-2">
//           <BiPackage className="text-secondary-600" size={16} />
//           <span className="font-poppins font-semibold text-[18px] text-title">
//             {row.libelle}
//           </span>
//         </div>
//       ),
//     },
//     {
//       field: "description",
//       headerName: "Description",
//       width: 250,
//       renderCell: ({ row }) => (
//         <span className="font-nunito text-[14px] text-text truncate">
//           {row.description || "Aucune description"}
//         </span>
//       ),
//     },
//     {
//       field: "quantity",
//       headerName: "Stock actuel",
//       width: 120,
//       renderCell: ({ row }) => (
//         <div className="text-center">
//           <span
//             className={`font-poppins font-bold text-[16px] text-montant px-2 py-1 ${getStockStatusColor(
//               row,
//             )}`}
//           >
//             {row.quantity}
//           </span>
//         </div>
//       ),
//     },
//     {
//       field: "seuilAlerte",
//       headerName: "Seuil d'alerte",
//       width: 120,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-medium text-[15px] text-subtitle">
//           {row.seuilAlerte}
//         </span>
//       ),
//     },
//     {
//       field: "status",
//       headerName: "Statut stock",
//       width: 130,
//       renderCell: ({ row }) => (
//         <span
//           className={`px-2 py-1 font-nunito text-[14px] font-medium ${getStockStatusColor(
//             row,
//           )}`}
//         >
//           {getStockStatusLabel(row)}
//         </span>
//       ),
//     },
//     {
//       field: "isActive",
//       headerName: "Actif",
//       width: 80,
//       renderCell: ({ row }) => (
//         <span
//           className={`px-2 py-1 font-nunito text-[14px] font-medium ${
//             row.isActive
//               ? "bg-green-100 text-green-800"
//               : "bg-red-100 text-red-800"
//           }`}
//         >
//           {row.isActive ? "Oui" : "Non"}
//         </span>
//       ),
//     },
//     {
//       field: "actions",
//       type: "actions",
//       headerName: "Actions",
//       width: 120,
//       getActions: ({ row }) => [
//         <GridActionsCellItem
//           icon={<BiShow size={20} />}
//           label="Détails"
//           onClick={() => handleShowDetails(row)}
//           color="primary"
//           className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
//         />,
//         <GridActionsCellItem
//           icon={<BiHistory size={20} />}
//           label="Historique"
//           onClick={() => handleShowHistory(row)}
//           color="secondary"
//           className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
//         />,
//         <GridActionsCellItem
//           icon={<BiEdit size={20} />}
//           label="Modifier"
//           onClick={() => handleEditProduct(row)}
//           color="success"
//           className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
//         />,
//         <GridActionsCellItem
//           icon={<BiTrash size={20} />}
//           label="Supprimer"
//           onClick={() => handleDeleteProduct(row)}
//           color="error"
//           className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
//         />,
//       ],
//     },
//   ];

//   // Filtrer les produits
//   const filteredProducts = products.filter((product) => {
//     const matchesSearch =
//       !searchTerm ||
//       product.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       product.description?.toLowerCase().includes(searchTerm.toLowerCase());

//     const matchesStatus =
//       !statusFilter ||
//       (statusFilter === "active" && product.isActive) ||
//       (statusFilter === "inactive" && !product.isActive) ||
//       (statusFilter === "low-stock" &&
//         getStockStatus(product) === "low-stock") ||
//       (statusFilter === "out-of-stock" &&
//         getStockStatus(product) === "out-of-stock");

//     return matchesSearch && matchesStatus;
//   });

//   // Statistiques
//   const totalProducts = products.length;
//   const activeProducts = products.filter((p) => p.isActive).length;
//   const lowStockProducts = products.filter(
//     (p) => getStockStatus(p) === "low-stock",
//   ).length;
//   const outOfStockProducts = products.filter(
//     (p) => getStockStatus(p) === "out-of-stock",
//   ).length;

//   return (
//     <div className="md:p-6">
//       {/* En-tête */}
//       <div className="mb-6">
//         <h1 className="font-poppins font-semibold text-[18px] text-title sm:text-3xl">
//           Gestion des Produits
//         </h1>
//         <p className="font-nunito text-[14px] text-text mt-2">
//           Gérez l'inventaire de vos produits et suivez les mouvements de stock
//         </p>
//       </div>

//       {/* Barre d'actions et recherche */}
//       <div className="bg-white p-4 shadow-sm border border-gray-200 mb-6">
//         <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
//           <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
//             {/* Barre de recherche */}
//             <div className="relative flex-1 sm:w-64">
//               <input
//                 type="text"
//                 placeholder="Rechercher un produit..."
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="w-full pl-3 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//               />
//             </div>

//             {/* Filtre par statut */}
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//             >
//               <option value="">Tous les statuts</option>
//               <option value="active">Actifs</option>
//               <option value="inactive">Inactifs</option>
//               <option value="low-stock">Stock faible</option>
//               <option value="out-of-stock">Rupture de stock</option>
//             </select>
//           </div>

//           {/* Bouton Ajouter */}
//           <button
//             onClick={handleAddProduct}
//             className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white hover:bg-secondary-700 transition-colors font-nunito text-[14px] font-semibold"
//           >
//             <BiPlus size={16} />
//             Ajouter un produit
//           </button>
//         </div>
//       </div>

//       {/* Statistiques */}
//       <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//         <div className="bg-white p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiPackage className="text-blue-600" size={20} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Total produits
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {totalProducts}
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiCheckCircle className="text-green-600" size={20} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Produits actifs
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {activeProducts}
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <FiAlertTriangle className="text-yellow-600" size={20} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Stock faible
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {lowStockProducts}
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiXCircle className="text-red-600" size={20} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Rupture de stock
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {outOfStockProducts}
//           </p>
//         </div>
//       </div>

//       {/* Tableau des produits */}
//       <div className="bg-white shadow-sm border border-gray-200 overflow-hidden flex-1">
//         <DataTable
//           title="Liste des produits"
//           rows={filteredProducts}
//           columns={columns}
//           loading={loading}
//           showToolbar={false}
//           autoHeight={true}
//           className="font-nunito text-[14px]"
//         />
//       </div>

//       {/* Modal Ajout/Modification Produit */}
//       {showProductModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white w-full max-w-md">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 {isEdit ? "Modifier le produit" : "Ajouter un produit"}
//               </h2>
//               <button
//                 onClick={() => setShowProductModal(false)}
//                 className="p-1 hover:bg-gray-100 transition-colors"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <form
//               onSubmit={
//                 isEdit ? handleUpdateSubmitProduct : handleSubmitProduct
//               }
//               className="p-4 space-y-4"
//             >
//               <div>
//                 <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                   Nom du produit *
//                 </label>
//                 <input
//                   type="text"
//                   required
//                   value={productForm.libelle}
//                   onChange={(e) =>
//                     setProductForm({
//                       ...productForm,
//                       libelle: e.target.value,
//                     })
//                   }
//                   className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                 />
//               </div>

//               <div>
//                 <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                   Description
//                 </label>
//                 <textarea
//                   value={productForm.description}
//                   onChange={(e) =>
//                     setProductForm({
//                       ...productForm,
//                       description: e.target.value,
//                     })
//                   }
//                   rows={3}
//                   className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                 />
//               </div>

//               <div
//                 className={`grid ${
//                   !isEdit ? "grid-cols-2" : "grid-cols-1"
//                 } gap-4`}
//               >
//                 {!isEdit && (
//                   <div>
//                     <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                       Quantité initiale
//                     </label>
//                     <input
//                       type="number"
//                       min="0"
//                       value={productForm.quantity}
//                       onChange={(e) =>
//                         setProductForm({
//                           ...productForm,
//                           quantity: parseInt(e.target.value),
//                         })
//                       }
//                       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                     />
//                   </div>
//                 )}

//                 <div>
//                   <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                     Seuil d'alerte
//                   </label>
//                   <input
//                     type="number"
//                     min="1"
//                     value={productForm.seuilAlerte}
//                     onChange={(e) =>
//                       setProductForm({
//                         ...productForm,
//                         seuilAlerte: parseInt(e.target.value),
//                       })
//                     }
//                     className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                   />
//                 </div>
//               </div>

//               <div className="flex items-center gap-2">
//                 <input
//                   type="checkbox"
//                   checked={productForm.isActive}
//                   onChange={(e) =>
//                     setProductForm({
//                       ...productForm,
//                       isActive: e.target.checked,
//                     })
//                   }
//                   className="border-gray-300 text-secondary-600 focus:ring-secondary-500"
//                 />
//                 <label className="font-poppins font-medium text-[15px] text-subtitle">
//                   Produit actif
//                 </label>
//               </div>

//               <div className="flex justify-end gap-3 pt-4">
//                 <button
//                   type="button"
//                   onClick={() => setShowProductModal(false)}
//                   className="px-4 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
//                 >
//                   Annuler
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={modalLoading}
//                   className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors disabled:opacity-50"
//                 >
//                   {isEdit
//                     ? modalLoading
//                       ? "Modification..."
//                       : "Modifier le produit"
//                     : modalLoading
//                       ? "Création..."
//                       : "Créer le produit"}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Modal Détails Produit */}
//       {showDetailsModal && currentProduct && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Détails du produit
//               </h2>
//               <button
//                 onClick={() => setShowDetailsModal(false)}
//                 className="p-1 hover:bg-gray-100 transition-colors"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <div className="p-4 space-y-4">
//               {modalLoading ? (
//                 <div className="flex justify-center py-8">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
//                 </div>
//               ) : (
//                 <>
//                   <div className="grid grid-cols-2 gap-4">
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Nom
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {currentProduct.libelle}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Statut
//                       </p>
//                       <span
//                         className={`px-2 py-1 font-nunito text-[14px] font-medium ${getStockStatusColor(
//                           currentProduct,
//                         )}`}
//                       >
//                         {getStockStatusLabel(currentProduct)}
//                       </span>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Stock actuel
//                       </p>
//                       <p className="font-poppins font-bold text-[16px] text-montant">
//                         {currentProduct.quantity}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Seuil d'alerte
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {currentProduct.seuilAlerte}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Actif
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {currentProduct.isActive ? "Oui" : "Non"}
//                       </p>
//                     </div>
//                   </div>

//                   {currentProduct.description && (
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle mb-1">
//                         Description
//                       </p>
//                       <p className="font-nunito text-[14px] text-text">
//                         {currentProduct.description}
//                       </p>
//                     </div>
//                   )}
//                   <hr />

//                   <div className="flex justify-between items-center gap-1">
//                     {/* Bouton Historique */}
//                     <button
//                       onClick={() => {
//                         setShowDetailsModal(false);
//                         handleShowHistory(currentProduct);
//                       }}
//                       className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
//                     >
//                       <BiHistory size={12} />
//                       Historique
//                     </button>

//                     {/* Bouton Éditer */}
//                     <button
//                       onClick={() => {
//                         setShowDetailsModal(false);
//                         handleEditProduct(currentProduct);
//                       }}
//                       className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
//                     >
//                       <BiEdit size={12} />
//                       Modifier
//                     </button>

//                     {/* Menu déroulant pour les mouvements */}
//                     <div className="relative group">
//                       <button className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors">
//                         <BiTrendingUp size={12} />
//                         Mouvement
//                       </button>
//                       <div className="absolute left-0 bottom-10 mt-1 w-48 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
//                         <button
//                           onClick={() => {
//                             setShowDetailsModal(false);
//                             handleShowMovement(currentProduct, "ENTREE");
//                           }}
//                           className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-[14px] text-green-700 hover:bg-green-50"
//                         >
//                           <BiTrendingUp size={14} />
//                           Entrée stock
//                         </button>
//                         <button
//                           onClick={() => {
//                             setShowDetailsModal(false);
//                             handleShowMovement(currentProduct, "SORTIE");
//                           }}
//                           className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-[14px] text-red-700 hover:bg-red-50"
//                         >
//                           <BiTrendingDown size={14} />
//                           Sortie stock
//                         </button>
//                         <button
//                           onClick={() => {
//                             setShowDetailsModal(false);
//                             handleShowMovement(currentProduct, "AJUSTEMENT");
//                           }}
//                           className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-[14px] text-blue-700 hover:bg-blue-50"
//                         >
//                           <BiAdjust size={14} />
//                           Ajustement
//                         </button>
//                         <button
//                           onClick={() => {
//                             setShowDetailsModal(false);
//                             handleShowMovement(currentProduct, "PERDU");
//                           }}
//                           className="flex items-center gap-2 w-full px-3 py-2 font-nunito text-[14px] text-orange-700 hover:bg-orange-50"
//                         >
//                           <BiXCircle size={14} />
//                           Marquer perte
//                         </button>
//                       </div>
//                     </div>

//                     {/* Bouton Supprimer */}
//                     <button
//                       onClick={() => {
//                         setShowDetailsModal(false);
//                         handleDeleteProduct(currentProduct);
//                       }}
//                       className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
//                     >
//                       <BiTrash size={12} />
//                       Supprimer
//                     </button>
//                   </div>
//                 </>
//               )}
//             </div>

//             <div className="p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={() => setShowDetailsModal(false)}
//                 className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
//               >
//                 Fermer
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Modal Historique */}
//       {showHistoryModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Historique des mouvements
//               </h2>
//               <button
//                 onClick={() => setShowHistoryModal(false)}
//                 className="p-1 hover:bg-gray-100 transition-colors"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <div className="p-4">
//               {modalLoading ? (
//                 <div className="flex justify-center py-8">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
//                 </div>
//               ) : historyReport.length === 0 ? (
//                 <p className="text-center font-nunito text-[14px] text-text py-8">
//                   Aucun mouvement enregistré
//                 </p>
//               ) : (
//                 <div className="space-y-3">
//                   {historyReport.map((movement, index) => (
//                     <div
//                       key={index}
//                       className="flex items-center justify-between p-3 border border-gray-200"
//                     >
//                       <div className="flex items-center gap-3">
//                         {getMovementTypeIcon(movement.type)}
//                         <div>
//                           <p className="font-poppins font-semibold text-[18px] text-title">
//                             {getMovementTypeLabel(movement.type)}
//                           </p>
//                           <p className="font-nunito text-[14px] text-text">
//                             {movement.motif}
//                           </p>
//                         </div>
//                       </div>
//                       <div className="text-right">
//                         <p
//                           className={`font-poppins font-bold text-[16px] text-montant ${
//                             movement.type === "ENTREE"
//                               ? "text-green-600"
//                               : "text-red-600"
//                           }`}
//                         >
//                           {movement.type === "ENTREE" ? "+" : "-"}
//                           {movement.quantity}
//                         </p>
//                         <p className="font-nunito text-[14px] text-text">
//                           {new Date(movement.created_at).toLocaleDateString(
//                             "fr-FR",
//                           )}
//                         </p>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             <div className="p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={() => setShowHistoryModal(false)}
//                 className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
//               >
//                 Fermer
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Modal Mouvement de stock */}
//       {showMovementModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white w-full max-w-md">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 {movementType === "ENTREE" && "Entrée de stock"}
//                 {movementType === "SORTIE" && "Sortie de stock"}
//                 {movementType === "AJUSTEMENT" && "Ajustement de stock"}
//                 {movementType === "PERDU" && "Marquer comme perdu"}
//               </h2>
//               <button
//                 onClick={() => setShowMovementModal(false)}
//                 className="p-1 hover:bg-gray-100 transition-colors"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <form onSubmit={handleSubmitMovement} className="p-4 space-y-4">
//               <div>
//                 <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                   Quantité *
//                 </label>
//                 <input
//                   type="number"
//                   min="1"
//                   required
//                   value={movementForm.quantity}
//                   onChange={(e) =>
//                     setMovementForm({
//                       ...movementForm,
//                       quantity: parseInt(e.target.value),
//                     })
//                   }
//                   className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                 />
//               </div>

//               <div>
//                 <label className="block font-poppins font-medium text-[15px] text-subtitle mb-1">
//                   Motif *
//                 </label>
//                 <textarea
//                   required
//                   value={movementForm.motif}
//                   onChange={(e) =>
//                     setMovementForm({
//                       ...movementForm,
//                       motif: e.target.value,
//                     })
//                   }
//                   rows={3}
//                   placeholder="Raison du mouvement..."
//                   className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//                 />
//               </div>

//               <div className="flex justify-end gap-3 pt-4">
//                 <button
//                   type="button"
//                   onClick={() => setShowMovementModal(false)}
//                   className="px-4 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
//                 >
//                   Annuler
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={modalLoading}
//                   className="px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors disabled:opacity-50"
//                 >
//                   {modalLoading ? "Enregistrement..." : "Enregistrer"}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Modal de confirmation de suppression */}
//       {deleteConfirm && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white p-6 max-w-md w-full">
//             <div className="flex items-center space-x-3 mb-4">
//               <div className="p-2 bg-red-50">
//                 <FiTrash2 className="text-red-500" size={24} />
//               </div>
//               <h3 className="font-poppins font-semibold text-[18px] text-title">
//                 Confirmer la suppression
//               </h3>
//             </div>
//             <p className="font-nunito text-[14px] text-text mb-6">
//               Êtes-vous sûr de vouloir supprimer le produit "
//               {deleteConfirm.libelle}" ? Cette action est irréversible.
//             </p>
//             <div className="flex space-x-3">
//               <button
//                 onClick={() => setDeleteConfirm(null)}
//                 className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 font-semibold transition-all duration-200 font-nunito text-[14px] text-action"
//               >
//                 Annuler
//               </button>
//               <button
//                 onClick={handleConfirmDelete}
//                 className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 font-semibold transition-all duration-200 font-nunito text-[14px] text-action"
//               >
//                 Supprimer
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
